"use client";

import classNames from "classnames";
import inputStyles from "../input/Input.module.scss";
import styles from "./InputOtp.module.scss";
import { useEffect, useRef, useState } from "react";

type InputOtpProps = {
  onSubmit: (code: string) => void;
  onChange: (value: string) => void;
  disabled?: boolean;
  label?: string;
  hasError?: boolean;
};

const InputOtp = ({
  onSubmit,
  onChange,
  disabled = false,
  label,
  hasError = false,
}: InputOtpProps) => {
  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs: any = useRef([]);

  const handleChange = (value: string, index: number) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < otp.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    const joined = newOtp.join("");
    onChange(joined);

    if (newOtp.every((d) => d !== "")) {
      onSubmit(joined);
    }
  };

  const handleKeyDown = (event: any, index: number) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const pasteData = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (/^\d{1,6}$/.test(pasteData)) {
      event.preventDefault();
      const newOtp = Array(6)
        .fill("")
        .map((_, i) => pasteData[i] || "");
      setOtp(newOtp);
      const joined = newOtp.join("");
      onChange(joined);
      inputRefs.current[5]?.focus();

      if (newOtp.every((digit) => digit !== "")) {
        onSubmit(joined);
      }
    }
  };

  useEffect(() => {
    if (!disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [disabled]);

  return (
    <div className={inputStyles.input}>
      {label && (
        <label
          className={classNames(
            inputStyles.input__label,
            inputStyles["input__label--small"],
          )}
        >
          <span>{label}</span>
        </label>
      )}

      <div onPaste={handlePaste} className={styles["comp__row"]}>
        {otp.map((digit, index) => (
          <div
            key={index}
            className={classNames(
              inputStyles["input__field"],
              inputStyles["input__field--size-medium"],
              hasError
                ? inputStyles["input__field--error"]
                : inputStyles["input__field--default"],
              inputStyles["input__field--large"],
              disabled && inputStyles["input__field--disabled"],
              styles["comp__cell"],
              hasError && styles["comp__cellError"],
            )}
          >
            <input
              ref={(el: any) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              aria-label={`Digit ${index + 1}`}
              pattern="\d*"
              maxLength={1}
              value={digit}
              disabled={disabled}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export { InputOtp };
